import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ScrollableContainer } from '@streamr/tv-ui';
import { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { RecordingService } from '../services/recording/RecordingService';
import { formatDate } from '../utils/time';
import { RecordingsList } from './RecordingsList';
import { StreamrTvLogo } from './shared/Logo';
const formatDateDiff = (diffSecs) => {
    const hours = Math.floor(diffSecs / 3600);
    const minutes = Math.floor((diffSecs % 3600) / 60);
    if (hours > 0) {
        return `${hours} hours`;
    }
    return `${minutes} minutes`;
};
export function ViewerPreLive({ scheduledStartTimestampMs, broadcastStreamId, broadcasterUserId, roundedCorners = true, }) {
    const liveIn = useMemo(() => {
        if (!scheduledStartTimestampMs) {
            return null;
        }
        const diff = scheduledStartTimestampMs - Date.now();
        return formatDateDiff(diff / 1000);
    }, [scheduledStartTimestampMs]);
    const startDate = useMemo(() => {
        if (!scheduledStartTimestampMs) {
            return null;
        }
        return new Date(scheduledStartTimestampMs);
    }, [scheduledStartTimestampMs]);
    const recordingService = RecordingService.getNotificationInterface();
    const recordingsList = RecordingService.getOutput().useRecordingsList();
    useEffect(() => {
        if (broadcastStreamId) {
            RecordingService.getNotificationInterface().notifyFetchRecordingsList(broadcasterUserId, broadcastStreamId, false);
        }
        return () => {
            RecordingService.getNotificationInterface().notifyResetRecordingsList();
        };
    }, [broadcastStreamId, broadcasterUserId]);
    return (_jsx(Background, { "$roundedCorners": roundedCorners, children: _jsx(Wrapper, { children: _jsx(ScrollableContainer, { children: _jsxs(AbsoluteContainer, { "$hasRecordings": recordingsList !== undefined &&
                        recordingsList.recordings.length > 0, children: [_jsxs(InfoBox, { children: [_jsx(StreamrTvLogo, {}), _jsxs("div", { children: [!liveIn && (_jsx(LiveIn, { children: "No broadcast at the moment" })), liveIn !== null && startDate !== null && (_jsxs("div", { children: [_jsxs(LiveIn, { children: ["Live in ", liveIn] }), _jsxs(DateContainer, { children: [formatDate(startDate, 'd LLLL'), " at", ' ', formatDate(startDate, 'h:mmaaa')] })] }))] })] }), recordingsList &&
                            recordingsList.recordings.length > 0 && (_jsxs(_Fragment, { children: [_jsxs(RecordingsBox, { children: [_jsx(RecordingsBoxTitle, { children: "Past Recordings" }), _jsx(RecordingsList, { recordings: recordingsList.recordings, variant: "viewer", hasMore: recordingsList.hasMore, status: recordingsList.status, onLoadMore: () => {
                                                recordingService.notifyFetchMoreRecordings();
                                            } })] }), _jsx(Spacer, {})] }))] }) }) }) }));
}
const Background = styled.div `
    display: block;
    height: 100%;
    width: 100%;

    position: absolute;
    top: 0;
    left: 0;

    // Show the background image
    &::before {
        display: block;
        content: '';
        width: 100%;
        height: 100%;
        background-image: url('/prelive.jpg');
        background-repeat: no-repeat;
        background-size: cover;
        border-radius: ${({ $roundedCorners }) => $roundedCorners ? '1rem' : '0'};
    }
`;
const AbsoluteContainer = styled.div `
    position: absolute;
    left: 0;
    right: 0;
    ${({ $hasRecordings }) => ($hasRecordings ? 'top: 40%;' : 'bottom: 0;')}
    bottom: 0;
    z-index: 1;
`;
const Wrapper = styled.div `
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
`;
const InfoBox = styled.div `
    display: grid;
    grid-template-rows: 1fr auto;
    margin: 1.5rem;
    padding: 1.5rem;
    max-width: 20.5rem;
    height: 13.5rem;
    box-sizing: border-box;
    background: var(--tv-ui-color-15);
    backdrop-filter: blur(3rem);
    border-radius: 2rem;
    font-family: "IBM Plex Sans", "Helvetica Neue", Arial, sans-serif;
    font-style: normal;
    font-weight: 400;
    font-size: 1.5rem;
`;
const RecordingsBox = styled.div `
    padding: 1.5rem;
    margin: 1.5rem;
    background: var(--tv-ui-color-15);
    backdrop-filter: blur(3rem);
    border-radius: 2rem;
    box-sizing: border-box;
`;
const RecordingsBoxTitle = styled.h2 `
    font-size: 1.125rem;
    font-weight: 500;
    color: var(--tv-ui-color-3);
    margin-bottom: 1.5rem;
`;
const Spacer = styled.div `
    height: 1px;
`;
const LiveIn = styled.div `
    letter-spacing: -0.02em;
    color: var(--tv-ui-color-3);
`;
const DateContainer = styled.div `
    letter-spacing: -0.02em;
    color: var(--tv-ui-color-3);
`;
